import { Component, OnInit } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LearningDialogComponent } from '../../../components/dialogs/learning-dialog/learning-dialog.component';
import {
  LearningDraftDialogComponent,
  LearningDraftResult,
} from '../../../components/dialogs/learning-draft-dialog/learning-draft-dialog.component';
import { AuthService, UserRoles } from '../../../services/auth.service';
import {
  ConversationDetail,
  ConversationFacet,
  ConversationMessage,
  ConversationRating,
  ConversationRow,
  ConversationsService,
} from '../../../services/conversations.service';
import { Learning, LearningInput, LearningService } from '../../../services/learning.service';

/**
 * Konversationen durchgehen: Sitzungen filtern, den Verlauf lesen.
 *
 * Die Liste ist bewusst nach Sitzung gebaut, nicht nach Ereignis — ein Daumen
 * runter ohne das Gespräch davor ist kaum zu bewerten. Filter und Suche
 * greifen deshalb ebenfalls auf der Sitzung: gesucht wird das Gespräch, in dem
 * ein Wort vorkam.
 *
 * Der Verlauf kommt aus denselben Turn-Ereignissen wie die Liste. Nur für alte
 * Sitzungen ohne Turn-Ereignisse fällt das Backend auf den LangGraph-
 * Checkpointer zurück; dann steht `source = 'checkpointer'` in der Antwort und
 * die Bewertungen lassen sich seltener genau zuordnen.
 */
@Component({
  selector: 'app-analytics-conversations',
  templateUrl: './conversations.component.html',
  styleUrls: ['./conversations.component.scss'],
})
export class ConversationsComponent implements OnInit {
  rows: ConversationRow[] = [];
  bots: ConversationFacet[] = [];
  courses: ConversationFacet[] = [];
  total = 0;
  loading = false;

  readonly dayPresets = [7, 30, 90];
  readonly allValue = '__all__';
  readonly limit = 25;

  days = 30;
  botId: string | null = null;
  courseId: string | null = null;
  rating: ConversationRating = 'any';
  sort: 'recent' | 'thumbs_down' = 'recent';
  search = '';
  page = 1;

  openSessionId: string | null = null;
  detail: ConversationDetail | null = null;
  detailLoading = false;

  private search$ = new Subject<string>();

  constructor(
    private service: ConversationsService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private dialogService: NbDialogService,
    private learningService: LearningService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    // Tippen darf nicht je Zeichen eine Volltextsuche auslösen — die läuft
    // über die Rohtexte aller Turns im Zeitraum.
    this.search$
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((value) => {
        this.search = value;
        this.page = 1;
        this.load();
      });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service
      .list({
        days: this.days,
        bot_id: this.botId,
        course_id: this.courseId,
        rating: this.rating,
        q: this.search.trim() || null,
        sort: this.sort,
        page: this.page,
        limit: this.limit,
      })
      .subscribe({
        next: (r) => {
          this.rows = r.data;
          this.total = r.total;
          this.bots = r.bots;
          this.courses = r.courses;
          this.loading = false;
        },
        error: () => {
          this.rows = [];
          this.total = 0;
          this.loading = false;
          this.toastr.danger(
            this.t.instant('pages.analytics.conversations.loadError'),
            this.t.instant('pages.analytics.conversations.title')
          );
        },
      });
  }

  onSearchInput(value: string): void {
    this.search$.next(value);
  }

  setDays(days: number): void {
    this.days = days;
    this.resetAndLoad();
  }

  setRating(value: ConversationRating): void {
    this.rating = value;
    this.resetAndLoad();
  }

  setBot(value: string): void {
    this.botId = value === this.allValue ? null : value;
    this.resetAndLoad();
  }

  setCourse(value: string): void {
    this.courseId = value === this.allValue ? null : value;
    this.resetAndLoad();
  }

  setSort(value: 'recent' | 'thumbs_down'): void {
    this.sort = value;
    this.resetAndLoad();
  }

  private resetAndLoad(): void {
    this.page = 1;
    this.openSessionId = null;
    this.detail = null;
    this.load();
  }

  get maxPage(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  setPage(page: number): void {
    if (page < 1 || page > this.maxPage) return;
    this.page = page;
    this.openSessionId = null;
    this.detail = null;
    this.load();
  }

  toggle(row: ConversationRow): void {
    if (this.openSessionId === row.session_id) {
      this.openSessionId = null;
      this.detail = null;
      return;
    }
    this.openSessionId = row.session_id;
    this.detail = null;
    this.detailLoading = true;
    this.service.detail(row.session_id).subscribe({
      next: (d) => {
        this.detail = d;
        this.detailLoading = false;
      },
      error: () => {
        this.detail = null;
        this.detailLoading = false;
      },
    });
  }

  trackRow(_: number, row: ConversationRow): string {
    return row.session_id;
  }

  // ── Curation: "Daraus lernen" ──

  /** Client-wide learnings change every agent — admin/manager only (the
   *  backend enforces it; the dialog just does not offer it otherwise). */
  private get canWriteClient(): boolean {
    const role = this.auth.accessTokenPayload$.value?.role;
    return role === UserRoles.Admin || role === UserRoles.Manager || role === UserRoles.SuperAdmin;
  }

  /** Only answers to a learner question: proactive messages (no question in
   *  the same turn) have nothing a learning could be found by — the backend
   *  refuses them with 422. */
  canLearnFrom(message: ConversationMessage): boolean {
    if (message.role !== 'assistant' || !message.event_id || !this.detail) return false;
    return this.detail.messages.some(
      (m) => m.role === 'user' && m.event_id === message.event_id && !!m.text?.trim()
    );
  }

  /** Step 1: expert note -> proposal (LearningDraftDialogComponent).
   *  Step 2: the normal learning form, prefilled; saving happens there. */
  learnFrom(message: ConversationMessage): void {
    if (!this.detail || !message.event_id || !this.canLearnFrom(message)) return;
    const question =
      this.detail.messages.find((m) => m.role === 'user' && m.event_id === message.event_id)?.text ?? '';
    this.dialogService.open(LearningDraftDialogComponent, {
      context: {
        eventId: message.event_id,
        question,
        answer: message.text,
        rating: message.rating ?? null,
        feedbackText: message.feedback_text ?? null,
        canWriteClient: this.canWriteClient,
        onProceed: (result: LearningDraftResult) => this.openLearningForm(message, result),
      },
    });
  }

  private openLearningForm(message: ConversationMessage, result: LearningDraftResult): void {
    this.dialogService.open(LearningDialogComponent, {
      context: {
        learning: null,
        scope: result.scope,
        prefill: result.prefill,
        onConfirm: (input: LearningInput) => {
          const req$ =
            result.scope === 'client'
              ? this.learningService.createClient(input)
              : this.learningService.createBot(result.botId, input);
          req$.subscribe({
            next: (saved: Learning) => {
              message.learning_ids = [...(message.learning_ids || []), saved.id];
              this.toastr.success(this.t.instant('pages.learnings.created'), '');
              if (!saved.embedded_at) {
                this.toastr.warning(this.t.instant('pages.learnings.searchable.noAfterSave'), '');
              }
            },
            error: (err) => {
              const detail = err?.error?.detail;
              this.toastr.danger(
                typeof detail === 'string' ? detail : this.t.instant('pages.learnings.saveFailed'),
                ''
              );
            },
          });
        },
      },
    });
  }
}
